import { useMemo } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TASK_ANSWER_TEXT_MAX_LENGTH, type TaskAnswerOptionType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';

interface TaskAnswersEditorProps {
    value: TaskAnswerOptionType[];
    disabled?: boolean;
    onChange: (next: TaskAnswerOptionType[]) => void;
}

const nextAnswerKey = (list: TaskAnswerOptionType[]): number =>
    list.reduce((max, item) => Math.max(max, Number(item.key) || 0), 0) + 1;

interface AnswerRowProps {
    item: TaskAnswerOptionType;
    index: number;
    isLast: boolean;
    disabled?: boolean;
    onChangeText: (key: number, text: string) => void;
    onDelete: (key: number) => void;
}

const AnswerRow = ({ item, index, isLast, disabled, onChangeText, onDelete }: AnswerRowProps) => {
    const { t } = useTranslation(['Tasks']);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.key,
        disabled,
    });

    return (
        <div
            ref={setNodeRef}
            className={`d-flex align-items-center gap-2 px-3 py-2 ${isLast ? '' : 'border-bottom'}`}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.6 : 1,
            }}
        >
            <button
                type="button"
                className="btn btn-link p-0 border-0 text-muted drag-handle"
                aria-label={t('answers.drag')}
                disabled={disabled}
                {...attributes}
                {...listeners}
            >
                <SolarIcon name="grip-vertical" />
            </button>
            <span className="text-muted text-center" style={{ width: 20 }}>
                {index + 1}
            </span>
            <Form.Control
                autoFocus
                value={item.text}
                disabled={disabled}
                maxLength={TASK_ANSWER_TEXT_MAX_LENGTH}
                placeholder={t('answers.placeholder')}
                aria-label={t('answers.placeholder')}
                onChange={(event) => onChangeText(item.key, event.target.value)}
            />
            <button
                type="button"
                className="btn btn-link p-0 border-0 text-danger"
                aria-label={t('answers.delete')}
                disabled={disabled}
                onClick={() => onDelete(item.key)}
            >
                <SolarIcon name="trash" />
            </button>
        </div>
    );
};

const TaskAnswersEditor = ({ value, disabled = false, onChange }: TaskAnswersEditorProps) => {
    const { t } = useTranslation(['Tasks']);
    const answers = value || [];
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
    const ids = useMemo(() => answers.map((item) => item.key), [answers]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const from = answers.findIndex((item) => item.key === active.id);
        const to = answers.findIndex((item) => item.key === over.id);
        if (from < 0 || to < 0) return;
        onChange(arrayMove(answers, from, to));
    };

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <label className="form-label text-muted mb-0">{t('answers.title')}</label>
                <button
                    type="button"
                    className="btn btn-link p-0"
                    disabled={disabled}
                    onClick={() => onChange([...answers, { key: nextAnswerKey(answers), text: '' }])}
                >
                    <SolarIcon name="plus" className="me-1" />
                    {t('answers.add')}
                </button>
            </div>
            {answers.length === 0 ? (
                <div className="border rounded text-center text-muted py-4 px-3">{t('answers.empty')}</div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                        <div className="border rounded">
                            {answers.map((item, index) => (
                                <AnswerRow
                                    key={item.key}
                                    item={item}
                                    index={index}
                                    isLast={index === answers.length - 1}
                                    disabled={disabled}
                                    onChangeText={(key, text) =>
                                        onChange(answers.map((row) => (row.key === key ? { ...row, text } : row)))
                                    }
                                    onDelete={(key) => onChange(answers.filter((row) => row.key !== key))}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};

export default TaskAnswersEditor;
